import { CanMatchFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { debounceTime, filter, iif, of, switchMap, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { appSelectors } from '@store/selectors/app.selectors';
import { filterNil } from 'ngxtension/filter-nil';
import { authSelectors } from '@store/selectors/auth.selectors';
import { environment } from '@env/environment';
import { STORAGE_KEYS, StorageService } from '@core/services/storage/storage.service';
import { addSeconds } from 'date-fns';
import { authActions } from '@store/actions/auth.actions';
import { AcRoutingService } from '@core/services/routing/ac-routing.service';

export const authGuard: CanMatchFn = () => {
  const oidcSecurityService = inject(OidcSecurityService);
  const storageService = inject(StorageService);
  const store = inject(Store<StoreState>);
  const cookiesConsent$ = store.select(appSelectors.cookiesConsent).pipe(filterNil(), take(1));
  const authChecking$ = store.select(authSelectors.checking).pipe(
    debounceTime(100),
    filter((value) => !value),
    take(1)
  );
  const router = inject(Router);
  return iif(() => environment.env !== 'local', cookiesConsent$, of(true)).pipe(
    switchMap(() => authChecking$),
    switchMap(() => oidcSecurityService.isAuthenticated$),
    map(({ allConfigsAuthenticated }) => {
      const navigation = router.getCurrentNavigation();
      if (allConfigsAuthenticated.every((config) => !config.isAuthenticated)) {
        if (navigation) {
          storageService.setItem(STORAGE_KEYS.REDIRECT, router.serializeUrl(navigation.extractedUrl), 'session');
          storageService.setItem(STORAGE_KEYS.REDIRECT_EXPIRY, String(addSeconds(new Date(), 60).getTime()), 'session');
        }
        store.dispatch(authActions.attemptLogin({}));
        return router.createUrlTree(AcRoutingService.ONELOGIN_ROUTE());
      }
      const redirect = storageService.getItem(STORAGE_KEYS.REDIRECT, 'session');
      const redirectExpiry = storageService.getItem(STORAGE_KEYS.REDIRECT_EXPIRY, 'session');
      storageService.removeItem(STORAGE_KEYS.REDIRECT, 'session');
      storageService.removeItem(STORAGE_KEYS.REDIRECT_EXPIRY, 'session');
      if (redirect != null && redirectExpiry != null) {
        try {
          return parseInt(redirectExpiry) > new Date().getTime() ? router.parseUrl(redirect) : true;
        } catch {
          return true;
        }
      }
      return true;
    })
  );
};
